import { h, Ref } from 'vue';
import { ChecklistTemplateBuilderEditable } from '@/ui/DTO/checklistTemplateBuilderEditable';
import { SelectOptions } from '@/ui/types';
import { t } from '@/ui/i18n/i18n';
import { dueAfterSelection } from '@/ui/components/issueTemplate/issueCreateTemplateModal/DueAfterSelection';
import { DueAfter } from '@/entities/issueTemplate';
import { required, Validator } from '@/entities/validation';
import { Task, useValidation } from '@/ui/concerns';
import AssigneesDropdown from '@/ui/pages/user/assignees/AssigneesDropdown.vue';
import { dropdownField } from '@/ui/components/form';

export function useChecklistTemplateBuilder(record: Ref<ChecklistTemplateBuilderEditable>) {
  const validate: Validator<ChecklistTemplateBuilderEditable> = category => [
    ...required(category.checklistId?.value?.toString() ?? '', 'checklistId'),
  ];

  const {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  } = useValidation(validate, record);

  const formFields = <T = number>(
    assigneeOptions: SelectOptions<T>,
    checklists: SelectOptions,
    fetchUsersTask: Task,
  ) => [
    dropdownField(
      {
        name: 'checklistId',
        label: t('checklist-template.checklistId'),
        value: record.value.checklistId.value,
        required: true,
        options: checklists,
        errors: validationErrors.value,
        disabled: false,
        placeholder: t('checklist-template.select-checklistId'),
      },
      value => {
        const optionSelected = checklists.find(checklist => checklist.value === value);
        record.value.checklistId.value = value;
        record.value.title.value = optionSelected ? optionSelected.label : '';
      },
    ),
    h('div', { class: 'field-box' }, [
      h('div', { class: 'label' }, t('checklist-template.assignees')),
      h(AssigneesDropdown, {
        props: {
          value: [
            ...record.value.assignees.value,
            ...record.value.assignedGroups.value,
          ],
          options: assigneeOptions,
          errors: validationErrors.value,
          disabled: false,
          hideExternalAssignee: true,
          placeholder: t('common.select-user-or-group'),
        },
        on: {
          input: (value: Array<string>) => {
            const assignees: Array<string> = [];
            const assignedGroups: Array<string> = [];

            value.forEach(v => {
              if (v.includes('user_')) {
                assignees.push(v);
              } else if (v.includes('group_')) {
                assignedGroups.push(v);
              }
            });

            record.value.assignees.value = assignees;
            record.value.assignedGroups.value = assignedGroups;
          },
          'user-created': (value: string|number) => {
            record.value.assignees.value.push(`user_${value}`);
            fetchUsersTask.run();
          },
        },
      }),
    ]),
    h('div', { class: 'field-box field-box--due-after' }, [
      dueAfterSelection({
        props: {
          value: record.value.dueIn.value,
        },
        on: {
          input: (value: DueAfter) => {
            record.value.dueIn.value = value;
          },
        },
      }),
    ]),
  ];

  return {
    formFields,
    showValidationErrors,
    hasNoValidationErrors,
  };
}
